import { normalizeLedgerFields } from "../../lib/ledger-field-compat";
import { useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ArrowRight, ArrowUp, Maximize2, Minimize2, RotateCcw, Sparkles } from "lucide-react";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import { money } from "../../lib/finance";
import LoadingLabel from "../../components/LoadingLabel";
import { MAX_SMART_MESSAGES, emptySmartDraft, type SmartDraft, type SmartMessage } from "./smart-add-draft";

/** Smart Add's conversation: describe entries in words, and review what it found once it has some. */
export default function SmartAddChat({ data, profile, draft, setDraft, error, setError, expanded, setExpanded, onPendingChange }: {
  data: DashboardData;
  profile: Owner;
  draft: SmartDraft;
  setDraft: Dispatch<SetStateAction<SmartDraft>>;
  /** Held by the caller, like the draft, so both outlast a trip to the review screen and back. */
  error: string;
  setError: (message: string) => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  /** Whether a reply is on its way, so the entry-method tabs stay put until it arrives. */
  onPendingChange: (value: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const editor = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const resize = () => {
      const el = editor.current;
      if (!el) return;
      const viewport = window.visualViewport?.height || window.innerHeight;
      const minimum = expanded ? Math.max(260, viewport * 0.6) : 220;
      const maximum = Math.max(minimum, viewport * (expanded ? 0.75 : 0.55));
      el.style.height = "auto";
      el.style.height = `${Math.min(maximum, Math.max(minimum, el.scrollHeight))}px`;
    };
    resize();
    window.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("resize", resize);
    };
  }, [draft.text, expanded, draft.stage]);
  const end = useRef<HTMLDivElement>(null),
    mounted = useRef(true),
    sending = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    onPendingChange(busy);
    return () => onPendingChange(false);
  }, [busy, onPendingChange]);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [draft.messages, busy]);
  async function send() {
    const text = draft.text.trim();
    if (!text || sending.current || draft.messages.length >= MAX_SMART_MESSAGES) return;
    sending.current = true;
    setBusy(true);
    setError("");
    const messages: SmartMessage[] = [
      ...draft.messages,
      { role: "user", content: text },
    ];
    try {
      const response = await fetch("/api/smart-add", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-Barnyard-Fields": "title" },
        body: JSON.stringify({ messages }),
        signal: AbortSignal.timeout(55000),
      });
      const result = normalizeLedgerFields(await response.json()) as {
        error?: string;
        message: string;
        ready: boolean;
        expenses: ExpenseInput[];
      };
      if (!response.ok)
        throw new Error(
          result.error || "Smart Add could not respond. Please try again.",
        );
      if (!mounted.current) return;
      setDraft((v) => ({
        ...v,
        messages: [...messages, { role: "assistant", content: result.message }],
        text: "",
        expenses: result.ready ? result.expenses : [],
      }));
    } catch (e) {
      if (mounted.current)
        setError(
          e instanceof Error && e.name !== "TimeoutError"
            ? e.message
            : "Smart Add took too long. Your description is still here; try again.",
        );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <div className="smart-add">
      <div
        className={`smart-messages${!draft.messages.length && !busy ? " empty" : ""}`}
        role="log"
        aria-label="Entry conversation"
        aria-live="polite"
      >
        {draft.messages.map((m, i) => (
          <div key={i} className={`smart-message ${m.role}`}>
            <small>{m.role === "user" ? profile : "Barnyard"}</small>
            <p>{m.content}</p>
          </div>
        ))}
        {busy && (
          <div className="smart-message assistant">
            <small>Barnyard</small>
            <p className="smart-thinking"><LoadingLabel>Working out the details</LoadingLabel></p>
          </div>
        )}
        <div ref={end} />
      </div>
      {draft.expenses.length > 0 && (
        <div className="smart-preview">
          <div>
            <span>READY TO REVIEW</span>
            <Sparkles size={18} />
          </div>
          <h4>
            {draft.expenses.length} entry
            {draft.expenses.length === 1 ? "" : "s"} found
          </h4>
          {draft.expenses.map((expense, index) => {
            const account = data.accounts.find(
              (a) => a.id === expense.accountId,
            );
            return (
              <p key={expense.requestId}>
                {index + 1}. {expense.title} ·{" "}
                {money(Number(expense.amount), account?.currency)}
              </p>
            );
          })}
          <button
            type="button"
            className="primary-button"
            disabled={busy}
            onClick={() => setDraft((v) => ({ ...v, stage: "review" }))}
          >
            Review & edit <ArrowRight size={18} />
          </button>
          <small>Nothing is saved until you confirm the list.</small>
        </div>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {draft.messages.length < MAX_SMART_MESSAGES ? (
        <form
          className="smart-composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <label className="sr-only" htmlFor="smart-description">
            Describe an expense, income or transfer
          </label>
          <textarea
            ref={editor}
            id="smart-description"
            rows={9}
            maxLength={8000}
            disabled={busy}
            value={draft.text}
            onChange={(e) =>
              setDraft((v) => ({ ...v, text: e.target.value }))
            }
            placeholder={
              draft.messages.length
                ? "Reply or correct a detail…"
                : "Describe or paste your transactions…"
            }
          />
          <div className="smart-composer-actions">
            <button
              type="button"
              className="smart-expand"
              aria-label={
                expanded ? "Collapse text area" : "Expand text area"
              }
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
              {expanded ? "Collapse" : "Expand"}
            </button>
            <span className="smart-character-count">
              {draft.text.length.toLocaleString()} / 8,000
            </span>
            <button
              type="submit"
              className="smart-send"
              aria-label="Send description"
              disabled={busy || !draft.text.trim()}
            >
              <ArrowUp size={22} />
            </button>
          </div>
        </form>
      ) : (
        <p className="smart-caption">
          You’ve reached the end of this short conversation. Review the
          draft, use Manual, or start fresh.
        </p>
      )}
      <div className="smart-footer">
        {draft.messages.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setDraft(emptySmartDraft());
              setError("");
            }}
          >
            <RotateCcw size={13} />
            Start fresh
          </button>
        )}
      </div>
      <details className="smart-privacy">
        <summary>Privacy & draft storage</summary>
        <p>
          Your description and account/category options are sent to an AI provider (OpenAI or Gemini).
          This conversation clears here after a verified save; unfinished
          drafts stay on this device for 24 hours. Started batches keep
          their save status until resolved.
        </p>
      </details>
    </div>
  );
}
